import { useEffect, useRef } from "react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";

import Spinner from "../../../../assets/icons/Spinner";
import Message from "../../../../i18n/Message";

import "./ChatPage.scss";
import { chatSelectors } from "../store/chatReducer";
import { deleteChat, loadChat } from "../store/chatActionCreators";
import PromptControl from "../prompt-form/PromptControl";
import ChatMessage from "./ChatMessage";
import ReplyLoadingState from "./ReplyLoadingState";
import ModelMessage from "./ModelMessage";
import TrashIcon from "../../../../assets/icons/TrashIcon";
import UploadIcon from "../../../../assets/icons/UploadIcon";
import SettingsIcon from "../../../../assets/icons/SettignsIcon";
import ChatSettingsDialog from "./settings/ChatSettingsDialog";
import ChevronLeftIcon from "../../../../assets/icons/ChevronLeftIcon";
import ExternalLinkIcon from "../../../../assets/icons/ExternalLinkIcon";
import ReplyErrorState from "./ReplyErrorState";

import CopyLinkDialog from "./copy-link/CopyLinkDialog";
import { scrollToChatBottom } from "./chatScrollUtils";
import Notify from "../../../shared/Notify";
import label from "../../../../i18n/label";
import useWebsocket from "../../../shared/ws/useWebsocket";
import ChatActions from "../store/chatActions";
import backend from "../../../../backend/backend";
import { linkToNewChatFolder } from "../../../../routes";
import { linkToNewChats } from "../../../../routes";
import NewWorkspaceLayout from "../../layout/NewWorkspaceLayout";
import ToolUseAction from "./tool-request/toolUseAction";
import { loadAssistant } from "../../assistants/store/assistantActionCreators";
import { assistantSelectors } from "../../assistants/store/assistantReducer";
import AssistantActions from "../../assistants/store/assistantActions";
import AssistantWelcomeMessage from "./AssistantWelcomeMessage";
import AssistantSystemMessage from "./AssistantSystemMessage";

const NewChatPage = () => {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { chatId, workspaceId } = useParams();
  const { socket: ws, isReady: wsReady } = useWebsocket();
  const chat = useSelector(chatSelectors.getChat);
  const messages = useSelector(chatSelectors.getMessages);
  const replyInProgress = useSelector(chatSelectors.getReplyInProgress);
  const replyError = useSelector(chatSelectors.getReplyError);
  const assistant = useSelector(assistantSelectors.getAssistant);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLinkCopy, setShowLinkCopy] = useState(false);

  const onMessageSuccessRef = useRef(null);
  const onMessageErrorRef = useRef(null);

  useEffect(() => {
    if (!chat || chat.uid !== chatId) {
      dispatch(loadChat(intl, workspaceId, chatId));
    }
  }, [dispatch, intl, workspaceId, chatId, chat]);

  // Load assistant if chat has assistantUid
  useEffect(() => {
    if (
      chat &&
      chat.assistantUid &&
      (!assistant || assistant.uid !== chat.assistantUid)
    ) {
      dispatch(loadAssistant(intl, workspaceId, chat.assistantUid));
    }
    if (chat && !chat.assistantUid && assistant) {
      dispatch({
        type: AssistantActions.LOADED_ASSISTANT,
        assistant: null,
      });
    }
  }, [chat?.assistantUid, assistant?.uid, dispatch, intl, workspaceId]);

  // Set up WebSocket message handlers
  useEffect(() => {
    if (!ws || !wsReady) {
      console.log("[WS] Not ready yet");
      return;
    }

    console.log("[WS] Setting up message handlers for chat:", chatId);

    ws.onmessage = function (event) {
      const message = event.data;
      const obj = JSON.parse(message);
      console.log("[WS] got ", obj);

      if (obj.error === true) {
        dispatch({
          type: ChatActions.MESSAGE_PART_ERROR,
          error: obj,
        });
        if (onMessageErrorRef.current) {
          onMessageErrorRef.current(obj);
        }
        return;
      }

      if (obj.type === "POST_CHAT_MESSAGE_REPLY_CHUNK") {
        // Dispatch thinking updates if we have content OR timing info (completion signal)
        if (obj.thinkingContent || obj.thinkingDurationMs != null) {
          dispatch({
            type: ChatActions.GOT_THINKING_CONTENT,
            thinkingContent: obj.thinkingContent,
            thinkingBlockIndex: obj.thinkingBlockIndex,
            thinkingStartTimeMs: obj.thinkingStartTimeMs,
            thinkingDurationMs: obj.thinkingDurationMs,
          });
        }
        if (obj.content) {
          dispatch({
            type: ChatActions.GOT_MESSAGE_PART,
            content: obj.content,
          });
        }
        if (obj.toolUses) {
          dispatch({
            type: ChatActions.GOT_TOOL_USES,
            toolUses: obj.toolUses,
          });
        }
      }

      if (obj.type === "POST_CHAT_MESSAGE_REPLY_FINISH") {
        console.log("[WS] FINISHED ", obj);

        dispatch({
          type: ChatActions.FINISHED_MESSAGE_PARTS,
          response: obj.messages,
        });

        if (onMessageSuccessRef.current) {
          onMessageSuccessRef.current();
        }
      }

      if (obj.type === "POST_CHAT_MESSAGE_AGENT_STEP") {
        console.log("[WS] AGENT STEP ", obj);
        dispatch({
          type: ChatActions.GOT_AGENT_STEP,
          step: obj,
        });
      }

      if (obj.type === "POST_CHAT_MESSAGE_AGENT_FINISHED") {
        console.log("[WS] AGENT FINISHED ", obj);
        dispatch({
          type: ChatActions.GOT_AGENT_FINISHED,
          finishData: obj,
        });

        // Reload chat data from server to get the final result and updated message
        console.log("[WS] Reloading chat data after agent session completion");
        dispatch(loadChat(intl, workspaceId, chatId));
      }

      setTimeout(scrollToChatBottom, 250);
    };

    ws.onerror = function (error) {
      console.error("[WS] Error: ", error);
      Notify.error(label(intl, "error_unexpected"));
      dispatch({
        type: ChatActions.MESSAGE_PART_ERROR,
        error: error,
      });
      if (onMessageErrorRef.current) {
        onMessageErrorRef.current(error);
      }
    };
  }, [ws, wsReady, chatId, workspaceId, dispatch, intl]);

  const onMessagePosted = (message, options, onSuccess, onError) => {
    // Check if WebSocket is ready
    if (!ws || !wsReady) {
      console.error("[WS] WebSocket not ready, cannot send message");
      Notify.error(label(intl, "error_unexpected"));
      onError && onError(new Error("WebSocket not ready"));
      return;
    }

    // Store callbacks in refs so the handlers can access them
    onMessageSuccessRef.current = onSuccess;
    onMessageErrorRef.current = onError;

    dispatch({
      type: ChatActions.POSTED_MESSAGE,
      message,
    });

    setTimeout(scrollToChatBottom, 300);

    console.log("[WS] Sending message:", message);

    try {
      ws.send(
        JSON.stringify({
          type: "POST_CHAT_MESSAGE",
          chatUid: chatId,
          workspaceUid: workspaceId,
          role: "user",
          content: message,
          pluginUids: options.pluginUids,
          kbaseUids: options.kbaseUids,
        })
      );
    } catch (error) {
      console.error("[WS] Failed to send message:", error);
      Notify.error(label(intl, "error_unexpected"));
      dispatch({
        type: ChatActions.MESSAGE_PART_ERROR,
        error: error,
      });
      onError && onError(error);
    }
  };

  const onDeleteConfirmed = () => {
    const onSuccess = () => {
      setShowDeleteConfirm(false);
      nav(linkToNewChats(workspaceId));
    };
    dispatch(
      deleteChat(intl, workspaceId, chat.uid, onSuccess, () =>
        setShowDeleteConfirm(false)
      )
    );
  };

  const onApproveToolUse = (toolUse, alwaysApprove, messageUid) => {
    console.log("onApproveToolUse ", toolUse, alwaysApprove);

    sendToolUseMessage(
      toolUse,
      alwaysApprove ? ToolUseAction.ALWAYS_APPROVE : ToolUseAction.APPROVE,
      messageUid
    );
  };

  const onRejectToolUse = (toolUse, messageUid) => {
    console.log("onRejectToolUse ", toolUse);
    sendToolUseMessage(toolUse, ToolUseAction.REJECT, messageUid);
  };

  const sendToolUseMessage = (toolUse, action, messageUid) => {
    dispatch({
      type: ChatActions.POSTED_TOOL_USE,
    });
    setTimeout(scrollToChatBottom, 500);
    try {
      ws.send(
        JSON.stringify({
          type: "POST_CHAT_MESSAGE",
          chatUid: chatId,
          workspaceUid: workspaceId,
          role: "user",
          content: "",
          toolUse: toolUse,
          toolUseAction: action,
          messageUid: messageUid,
        })
      );
    } catch (error) {
      console.error("[WS] Failed to send message:", error);
      Notify.error(label(intl, "error_unexpected"));
      dispatch({
        type: ChatActions.MESSAGE_PART_ERROR,
        error: error,
      });
    }
  };

  const loading = !chat || chat.uid !== chatId;

  return (
    <NewWorkspaceLayout contentPanelClassName="chat_details_main">
      <div className="body_content card">
        <div className="content_header">
          {!loading && (
            <div className="content_header_body">
              <div className="breadcrumbs">
                <div
                  onClick={() =>
                    nav(
                      chat.parentFolderUid
                        ? linkToNewChatFolder(workspaceId, chat.parentFolderUid)
                        : linkToNewChats(workspaceId)
                    )
                  }
                >
                  <ChevronLeftIcon
                    size={16}
                    strokeWidth="2"
                    color="rgb(103, 116, 142)"
                  />
                  <Message
                    id={
                      chat.parentFolderUid ? "back_to_folder" : "back_to_chats"
                    }
                  />
                </div>
              </div>
              <div className="title_row">
                <>
                  <h1 className="chat_title">{chat?.name}</h1>

                  <div className="title_actions">
                    <div className="confirm_container">
                      {showDeleteConfirm && (
                        <>
                          <div
                            className="cover"
                            onClick={() => setShowDeleteConfirm(false)}
                          ></div>
                          <div className="confirm_panel">
                            <button
                              className="btn bg-gradient-primary btn-sm"
                              onClick={onDeleteConfirmed}
                            >
                              <Message id="confirm_delete" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      className="btn no-btn tile-action"
                      onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                    >
                      <TrashIcon size="16" color="#344767" />
                      <Message id="delete" />
                    </button>
                    <button
                      className="btn no-btn tile-action"
                      onClick={() => setShowSettings(true)}
                    >
                      <SettingsIcon size="16" color="#344767" />
                      <Message id="settings" />
                    </button>
                    <button
                      className="btn no-btn tile-action"
                      onClick={() => setShowLinkCopy(true)}
                    >
                      <ExternalLinkIcon
                        size="16"
                        color="#344767"
                        strokeWidth="2"
                      />
                      <Message id="share_chat" />
                    </button>
                  </div>
                </>
              </div>
            </div>
          )}
        </div>

        <div className="content_container">
          <div className="content_container_absolute">
            <div className="content_width_container">
              {loading && <Spinner />}
              {!loading && (
                <div className="messages_feed">
                  {chat && assistant && (
                    <AssistantSystemMessage
                      assistant={assistant}
                      chat={chat}
                      setShowSettings={setShowSettings}
                    />
                  )}
                  {chat && !assistant && (
                    <ModelMessage
                      chat={chat}
                      setShowSettings={setShowSettings}
                    />
                  )}
                  {assistant && assistant.welcomeMessage && (
                    <AssistantWelcomeMessage assistant={assistant} chat={chat} />
                  )}
                  {messages.map((item, index) => {
                    if (item.role === "user" && item.toolUse) {
                      return null;
                    }

                    const isLastMessage = index === messages.length - 1; // Is the last message

                    return (
                      <ChatMessage
                        key={item.uid ?? item.content}
                        message={item}
                        chat={chat}
                        onApproveToolUse={onApproveToolUse}
                        onRejectToolUse={onRejectToolUse}
                        isLastMessage={isLastMessage}
                      />
                    );
                  })}
                  {replyInProgress && (
                    <ReplyLoadingState
                      chat={chat}
                      onApprove={onApproveToolUse}
                      onReject={onRejectToolUse}
                    />
                  )}
                  {replyError && <ReplyErrorState replyError={replyError} />}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="prompt_control_container">
          <div className="prompt_control_max_width">
            {!loading && (
              <PromptControl onSubmit={onMessagePosted} assistant={assistant} />
            )}
          </div>
        </div>
      </div>

      {showSettings && (
        <ChatSettingsDialog
          chat={chat}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showLinkCopy && (
        <CopyLinkDialog chat={chat} onClose={() => setShowLinkCopy(false)} />
      )}
    </NewWorkspaceLayout>
  );
};

export default NewChatPage;
